import React, { memo } from 'react'
import { usePersistedState } from '@cbgms/base/hooks/persisted-state/use-persisted-state'
import { IExpose, ShowNetPriceContext } from '@cbgms/components/src/order-parts/context/ShowNetPriceContext'
import { useIsPortal } from 'utils/Subscription/PermissionSelector'
import { useFeature } from '@cbgms/base/feature-flags'
import { usePriceRules } from '../../../settings/components/PriceMarkups/use-price-rules'

const _ShowNetPriceContextProvider: React.FC<{ value: IExpose }> = ({ value, children }) => (
  <ShowNetPriceContext.Provider value={value}>{children}</ShowNetPriceContext.Provider>
)
const MemoShowNetPriceContextProvider: React.FC<{ value: IExpose }> = memo(_ShowNetPriceContextProvider)

export const ShowNetPriceContextProvider: React.FC = ({ children }) => {
  const [showNetPrice, setShowNetPrice] = usePersistedState('showNetPrice', true)
  const [refreshTrigger, setRefreshTrigger] = React.useState(0)
  const isPortal = useIsPortal()
  const hasB2BPriceMarkup = useFeature('b2b-price-markup')
  const { isEnabled: isPriceRulesEnabled, refetch, refetchSettings } = usePriceRules()

  // Listen for manual refresh events
  React.useEffect(() => {
    const handleRefresh = () => {
      setRefreshTrigger(prev => prev + 1)
      // Refetch both settings and rules to ensure context updates
      refetchSettings()
      refetch() // Also refetch the actual rules data
    }

    window.addEventListener('refreshPriceContext', handleRefresh)
    return () => window.removeEventListener('refreshPriceContext', handleRefresh)
  }, [refetchSettings, refetch])

  const value = React.useMemo(() => {
    // Show "My Price" when:
    // For portal users: B2B price markup feature is enabled AND price rules are enabled
    // For non-portal users: price rules are enabled (regardless of B2B feature)
    const showMyPrice = isPortal ? hasB2BPriceMarkup && isPriceRulesEnabled : isPriceRulesEnabled

    return {
      hasBeenProvided: true,
      showNetPrice,
      setShowNetPrice,
      showMyPrice,
      refetchPriceRulesSettings: refetchSettings
    }
  }, [showNetPrice, isPortal, hasB2BPriceMarkup, isPriceRulesEnabled, refetchSettings, refreshTrigger])

  return <MemoShowNetPriceContextProvider value={value}>{children}</MemoShowNetPriceContextProvider>
}

export const { Consumer: ShowNetPriceContextConsumer } = ShowNetPriceContext
